/**
 * **«تحديث موقف المتابعة»** (0151 — قرار المالك 01/10/2026: «بدل سجل جديد، خلّيها تحديث موقف المتابعة»، وأصله: «نضيف خانة تم المتابعة بحيث يظهر إن العميل ده تم متابعته، ونضيف
 * التفاصيل الجديدة في المتابعات المستحقة بدل ما نضطر ندخل كل البيانات من الأول»).
 *
 * الاسم والرقم والبياع من المتابعة نفسها — لا يُكتبون. والجديد وحده: نوع التواصل ونتيجته وموقف العميل (تبدأ فارغة
 * كالنموذج — قرار المالك: «خلّيهم فاضيين علشان هو يملاها»)، والملاحظات، ويوم المتابعة القادمة، والأولوية من السابقة.
 * وفوقها سجلّ العميل كلّه ليعرف ما قيل قبل أن يكتب. والحفظ تواصلٌ جديد في السجلّ، والسابقة تُعلَّم «تمت المتابعة».
 */
import { useEffect, useState, type FormEvent } from 'react';
import { get, post, ApiError, type FollowUp, type HistoryResponse } from '../lib/api.ts';
import { CHANNELS, PRIORITIES, RESULTS, STATUSES, NEXT_REQUIRED_MSG, noNextNeeded , syncCompanyCustomer } from '../lib/labels.ts';
import { addDays, cairoToday, shortDate } from '../lib/format.ts';
import { PhoneActions } from '../components/PhoneActions.tsx';
import { Alert, DateInput, Field, Modal, Select, Spinner, TextArea } from '../components/ui.tsx';
import { CustomerTimeline } from './CustomerTimeline.tsx';

export function FollowUpDoneModal({ followUp, showSeller = false, onClose, onDone }: {
  followUp: FollowUp; showSeller?: boolean; onClose: () => void; onDone: (created: FollowUp) => void;
}) {
  const [history, setHistory] = useState<FollowUp[] | null>(null);
  const [form, setForm] = useState({ channel: '', result: '', customerStatus: '', notes: '', nextFollowUpOn: '', priority: followUp.priority });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form, v: string) => setForm(s => ({ ...s, [k]: v }));

  useEffect(() => {
    get<HistoryResponse>(`/follow-ups/history?phone=${encodeURIComponent(followUp.phone)}`)
      .then(h => setHistory(h.items)).catch(() => setHistory([]));
  }, [followUp.phone]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.nextFollowUpOn && !noNextNeeded(form.customerStatus, form.result)) { setError(NEXT_REQUIRED_MSG); return; }
    setBusy(true);
    try {
      const created = await post<FollowUp>(`/follow-ups/${followUp.id}/done`, {
        channel: form.channel, result: form.result, customerStatus: form.customerStatus,
        notes: form.notes.trim() || null, nextFollowUpOn: form.nextFollowUpOn || null, priority: form.priority,
      });
      onDone(created);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'تعذّر تسجيل المتابعة');
    } finally { setBusy(false); }
  }

  return (
    // كتب شيئًا ⇒ الإغلاق يسأل أولًا، وأثناء الحفظ لا إغلاق (قرار المالك 02/10/2026)
    <Modal title={`تحديث موقف المتابعة — ${followUp.customerName}`} size="xl" onClose={onClose} holdClose={busy}
      confirmClose={!!(form.channel || form.result || form.customerStatus || form.notes.trim() || form.nextFollowUpOn)}>
      <div className="space-y-4 lg:grid lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:items-start lg:gap-6 lg:space-y-0">
        <form onSubmit={onSubmit} aria-label="تسجيل المتابعة الجديدة" className="space-y-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
            <span className="font-semibold text-ink-900">{followUp.customerName}</span>
            <span dir="ltr" className="tabular-nums text-ink-600"><PhoneActions phone={followUp.phone} /></span>
            {showSeller && <span className="text-ink-500">· {followUp.salesName}</span>}
            {/* آخر ما قيل — قبل أن يكتب الجديد، ولو على الموبايل حيث السجلّ كلّه تحت النموذج */}
            {followUp.notes && (
              <p className="basis-full text-ink-700">
                <span className="font-semibold text-ink-500">آخر مرة (<bdi className="tabular-nums">{shortDate(followUp.contactedOn)}</bdi>): </span>
                {followUp.notes}
              </p>
            )}
          </div>
          <p className="text-sm text-ink-600">اكتب الجديد بس — الاسم والرقم محفوظين، وموقف المتابعة هيتحدّث، وكل اللي فات يفضل في <b>سجل العميل</b>.</p>
          {error && <Alert>{error}</Alert>}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="نوع التواصل" required>
              <Select name="channel" required placeholder="اختر…" options={CHANNELS} value={form.channel} onChange={e => set('channel', e.target.value)} />
            </Field>
            <Field label="نتيجة التواصل" required hint={form.result === 'NO_ANSWER' || form.result === 'UNREACHABLE' ? 'لو الرقم من بنك الأرقام: مش هيتحسب في التارجت غير لما العميل يرد عليك (02/10/2026)' : undefined}>
              <Select name="result" required placeholder="اختر…" options={RESULTS} value={form.result} onChange={e => { const v = e.target.value; setForm(cur => syncCompanyCustomer(cur, 'result', v)); }} />
            </Field>
            <Field label="موقف العميل" required>
              <Select name="customerStatus" required placeholder="اختر…" options={STATUSES} value={form.customerStatus} onChange={e => { const v = e.target.value; setForm(cur => syncCompanyCustomer(cur, 'customerStatus', v)); }} />
            </Field>
            <Field label="الأولوية">
              <Select name="priority" options={PRIORITIES} value={form.priority} onChange={e => set('priority', e.target.value)} />
            </Field>
          </div>
          <Field label="الجديد في المتابعة دي">
            <TextArea name="notes" rows={3} maxLength={2000} value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="العميل قال إيه؟ وصلتوا لإيه؟" />
          </Field>
          <Field label="يوم المتابعة القادمة" required={!noNextNeeded(form.customerStatus, form.result)}
            hint={noNextNeeded(form.customerStatus, form.result) ? 'مش لازم — العميل غير مهتم أو فقدناه' : 'لازم — هتكلّم العميل تاني إمتى؟'}>
            <DateInput name="nextFollowUpOn" required={!noNextNeeded(form.customerStatus, form.result)} min={addDays(cairoToday(), 1)} value={form.nextFollowUpOn} onChange={v => set('nextFollowUpOn', v)} />
          </Field>
          <div className="flex flex-wrap gap-2 pt-1">
            <button type="submit" className="btn-primary flex-1 bg-emerald-600 hover:bg-emerald-700 sm:flex-none sm:px-8" disabled={busy}>
              {busy ? 'جارٍ الحفظ…' : '💾 حفظ التحديث'}
            </button>
            <button type="button" className="btn-ghost" onClick={onClose}>إلغاء</button>
          </div>
        </form>

        <section aria-label="سجل العميل كله" className="border-t border-slate-200 pt-3 lg:rounded-lg lg:border lg:bg-slate-50/50 lg:p-3">
          <h3 className="mb-2 text-sm font-semibold text-ink-900">سجل العميل — كل تحديثات المتابعة</h3>
          <div className="max-h-72 overflow-y-auto pe-1 lg:max-h-[28rem]">
            {history === null ? <Spinner /> : <CustomerTimeline items={history} currentId={followUp.id} showSeller={showSeller} />}
          </div>
        </section>
      </div>
    </Modal>
  );
}
